/**
 * Admin · Referral Invites (Phase 2L-A81)
 *
 * Read-only operational visibility into the Review Follow-Up /
 * Invite-a-Neighbour / Voucher pipeline. Admin-only.
 *
 * Scope (locked):
 *   • View invite + voucher records, filter, search.
 *   • Conservative copy/open actions (invite URL, voucher code, linked
 *     booking, linked redeemed booking).
 *   • NO manual redeem / discount / delete actions.
 *   • Does NOT touch feature flags, email, Stripe, or booking logic.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Copy, RefreshCw, Search, Loader2, ExternalLink, FlaskConical,
  Mail, Ticket, CheckCircle2, AlertTriangle, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { PortalShell } from "@/components/PortalShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiError } from "@/lib/api";
import { formatDateAU } from "@/lib/formatDateAU";

const STATUS_PRESETS = [
  { id: "all",               label: "All" },
  { id: "sent",              label: "Sent" },
  { id: "voucher_generated", label: "Voucher generated" },
  { id: "voucher_applied",   label: "Voucher applied" },
  { id: "voucher_redeemed",  label: "Voucher redeemed" },
  { id: "expired",           label: "Expired" },
  { id: "email_bounced",     label: "Email bounced" },
  { id: "email_failed",      label: "Email failed" },
];

const SMOKE_OPTIONS = [
  { id: "real_only",  label: "Real records only" },
  { id: "all",        label: "All (real + smoke)" },
  { id: "smoke_only", label: "Smoke tests only" },
];

// Map voucher_status → pill tone + label.
const VOUCHER_STATUS_TONE = {
  issued:        { tone: "slate",   label: "Issued" },
  sent:          { tone: "blue",    label: "Sent" },
  applied:       { tone: "amber",   label: "Applied" },
  redeemed:      { tone: "emerald", label: "Redeemed" },
  expired:       { tone: "slate",   label: "Expired" },
  voided:        { tone: "rose",    label: "Voided" },
  reissued:      { tone: "slate",   label: "Reissued" },
  email_bounced: { tone: "rose",    label: "Bounced" },
};

const TONE_CLASS = {
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber:   "bg-amber-50 text-amber-700 border-amber-200",
  blue:    "bg-blue-50 text-blue-700 border-blue-200",
  slate:   "bg-slate-100 text-slate-600 border-slate-200",
  rose:    "bg-rose-50 text-rose-700 border-rose-200",
  violet:  "bg-violet-50 text-violet-700 border-violet-200",
};

function Pill({ tone = "slate", children, testId }) {
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${TONE_CLASS[tone] || TONE_CLASS.slate}`}
    >
      {children}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  return formatDateAU(iso);
}

function fmtMoney(amount) {
  if (amount == null) return "—";
  const n = Number(amount);
  if (Number.isNaN(n)) return "—";
  return `$${n.toFixed(0)}`;
}

async function copyToClipboard(text, successMsg = "Copied") {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(successMsg, { duration: 1500 });
  } catch {
    // Fallback — older browsers without async clipboard. Still surface
    // a "copied" toast so the UX doesn't look broken.
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand("copy"); } catch { /* swallow */ }
    document.body.removeChild(ta);
    toast.success(successMsg, { duration: 1500 });
  }
}

function buildInviteUrl(secureToken) {
  if (!secureToken) return "";
  // Same path the public invite email uses (see referral_invite_email.py).
  return `${window.location.origin}/book?invite=${encodeURIComponent(secureToken)}`;
}

export default function AdminReferralInvites() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusPreset, setStatusPreset] = useState("all");
  const [smoke, setSmoke] = useState("real_only");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { status_preset: statusPreset, smoke, limit: 500 };
      const res = await api.get("/admin/referral-invites", { params });
      setRows(res.data?.items || []);
    } catch (e) {
      const msg = formatApiError(e?.response?.data?.detail || e.message);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [statusPreset, smoke]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      return (
        (r.referrer_email || "").toLowerCase().includes(q) ||
        (r.referrer_first_name || "").toLowerCase().includes(q) ||
        (r.invitee_email || "").toLowerCase().includes(q) ||
        (r.invitee_first_name || "").toLowerCase().includes(q) ||
        (r.voucher_code || "").toLowerCase().includes(q) ||
        (r.referral_token || "").toLowerCase().includes(q) ||
        (r.referrer_booking_reference || "").toLowerCase().includes(q) ||
        (r.referrer_test_id || "").toLowerCase().includes(q) ||
        (r.voucher_redeemed_booking_ref || "").toLowerCase().includes(q)
      );
    });
  }, [rows, search]);

  const counts = useMemo(() => {
    return {
      total: rows.length,
      sent: rows.filter((r) => r.voucher_status === "sent").length,
      redeemed: rows.filter((r) => r.voucher_status === "redeemed").length,
      applied: rows.filter((r) => r.voucher_status === "applied").length,
      smoke: rows.filter((r) => r.is_smoke_test).length,
    };
  }, [rows]);

  return (
    <PortalShell
      title="Referral Invites"
      subtitle="Read-only visibility into the Review Follow-Up / Invite-a-Neighbour / Voucher pipeline. Admin-only."
    >
      <div data-testid="admin-referral-invites-page" className="space-y-6">
        {/* Phase 2L-A97 — explicit amber banner when forensic mode is on.
            Default real_only view excludes smoke + demo rows silently;
            the banner makes the bypass mode loud. */}
        {smoke !== "real_only" && (
          <div
            data-testid="referral-invites-forensic-banner"
            className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          >
            <span className="font-semibold">Forensic view:</span> smoke-test
            and demo/fixture invite rows are included
            ({smoke === "all" ? "real + smoke + demo" : "smoke only"}).
            Switch the filter back to <em>Real records only</em> to return
            to the live operational view.
          </div>
        )}

        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <SummaryTile label="Records" value={counts.total} icon={Ticket} />
          <SummaryTile label="Sent" value={counts.sent} icon={Mail} tone="blue" />
          <SummaryTile label="Applied" value={counts.applied} icon={Clock} tone="amber" />
          <SummaryTile label="Redeemed" value={counts.redeemed} icon={CheckCircle2} tone="emerald" />
          <SummaryTile label="Smoke" value={counts.smoke} icon={FlaskConical} tone="violet" />
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-3 bg-white border border-slate-200 rounded-2xl p-4">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="referral-invites-search"
              placeholder="Search referrer / invitee / voucher / booking ref…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <select
            data-testid="referral-invites-status-filter"
            value={statusPreset}
            onChange={(e) => setStatusPreset(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
          >
            {STATUS_PRESETS.map((p) => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>
          <select
            data-testid="referral-invites-smoke-filter"
            value={smoke}
            onChange={(e) => setSmoke(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
          >
            {SMOKE_OPTIONS.map((p) => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>
          <Button
            data-testid="referral-invites-refresh"
            type="button"
            variant="outline"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </Button>
        </div>

        {error && (
          <div
            data-testid="referral-invites-error"
            className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700"
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>{error}</div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div
              data-testid="referral-invites-empty"
              className="py-16 text-center text-slate-500"
            >
              {rows.length === 0
                ? "No invite records match the current filters yet."
                : "No records match the current search."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-700 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-3 py-3 text-left">Created</th>
                    <th className="px-3 py-3 text-left">Referrer</th>
                    <th className="px-3 py-3 text-left">Invitee</th>
                    <th className="px-3 py-3 text-left">Voucher</th>
                    <th className="px-3 py-3 text-left">Email</th>
                    <th className="px-3 py-3 text-left">Voucher status</th>
                    <th className="px-3 py-3 text-left">Expiry</th>
                    <th className="px-3 py-3 text-left">Redeemed</th>
                    <th className="px-3 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((row) => (
                    <InviteRow key={row.id} row={row} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Helper note */}
        <p className="text-xs text-slate-500">
          Read-only view. To modify voucher status or redeem manually,
          use the database tooling — this surface is intentionally
          conservative. Smoke-test rows have a <strong>SMOKE</strong>
          badge and are hidden by default.
        </p>
      </div>
    </PortalShell>
  );
}

function SummaryTile({ label, value, icon: Icon, tone = "slate" }) {
  return (
    <div className={`rounded-2xl border p-3 ${TONE_CLASS[tone] || TONE_CLASS.slate}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider opacity-80">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {label}
      </div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}

function InviteRow({ row }) {
  const voucherTone = VOUCHER_STATUS_TONE[row.voucher_status] || { tone: "slate", label: row.voucher_status || "—" };
  const emailTone = ({
    sent: "blue", queued: "slate", failed: "rose",
  })[row.invite_email_status] || "slate";
  const inviteUrl = buildInviteUrl(row.secure_invite_token);

  return (
    <tr data-testid={`referral-invite-row-${row.id}`} className="hover:bg-slate-50 align-top">
      <td className="px-3 py-3 whitespace-nowrap text-slate-600">
        <div>{fmtDate(row.created_at)}</div>
        {row.is_smoke_test && (
          <div className="mt-1">
            <Pill tone="violet" testId={`referral-invite-smoke-${row.id}`}>
              <FlaskConical className="w-3 h-3" /> SMOKE
            </Pill>
          </div>
        )}
        {row.is_demo && !row.is_smoke_test && (
          <div className="mt-1">
            <Pill tone="amber" testId={`referral-invite-demo-${row.id}`}>
              <FlaskConical className="w-3 h-3" /> DEMO
            </Pill>
          </div>
        )}
      </td>
      <td className="px-3 py-3">
        <div className="font-medium text-slate-900">
          {row.referrer_first_name || "—"}
        </div>
        <div className="text-xs text-slate-500 break-all">{row.referrer_email || "—"}</div>
        {row.referrer_booking_reference && (
          <div className="text-xs font-mono text-slate-500 mt-1">
            <Link
              to={`/admin/bookings/${encodeURIComponent(row.referrer_booking_reference)}`}
              className="hover:underline text-blue-700"
              data-testid={`referral-invite-referrer-booking-${row.id}`}
            >
              {row.referrer_booking_reference}
            </Link>
          </div>
        )}
        {row.referrer_test_id && !row.referrer_test_id.startsWith("SMOKE-") && (
          <div className="text-xs font-mono text-slate-400 mt-0.5">
            <Link
              to={`/admin/lab/${encodeURIComponent(row.referrer_test_id)}`}
              className="hover:underline"
              data-testid={`referral-invite-referrer-test-${row.id}`}
            >
              test: {row.referrer_test_id.slice(0, 12)}…
            </Link>
          </div>
        )}
      </td>
      <td className="px-3 py-3">
        <div className="font-medium text-slate-900">{row.invitee_first_name || "—"}</div>
        <div className="text-xs text-slate-500 break-all">{row.invitee_email || "—"}</div>
        {row.invitee_suburb && (
          <div className="text-xs text-slate-400 mt-0.5">{row.invitee_suburb}</div>
        )}
      </td>
      <td className="px-3 py-3">
        <div className="font-mono font-semibold text-slate-900 text-xs whitespace-nowrap">
          {row.voucher_code || "—"}
        </div>
        <div className="text-xs text-slate-500 mt-0.5">{fmtMoney(row.voucher_amount)} off</div>
      </td>
      <td className="px-3 py-3 whitespace-nowrap">
        <Pill tone={emailTone}>
          <Mail className="w-3 h-3" />
          {row.invite_email_status || "—"}
        </Pill>
        {row.sent_at && (
          <div className="text-xs text-slate-500 mt-1">{fmtDate(row.sent_at)}</div>
        )}
        {row.invite_email_error && (
          <div className="text-xs text-rose-600 mt-1 max-w-[180px] truncate" title={row.invite_email_error}>
            {row.invite_email_error}
          </div>
        )}
      </td>
      <td className="px-3 py-3 whitespace-nowrap">
        <Pill tone={voucherTone.tone} testId={`referral-invite-voucher-status-${row.id}`}>
          {voucherTone.label}
        </Pill>
        {row.voucher_applied_booking_ref && row.voucher_status === "applied" && (
          <div className="text-xs font-mono text-slate-500 mt-1">
            on{" "}
            <Link
              to={`/admin/bookings/${encodeURIComponent(row.voucher_applied_booking_ref)}`}
              className="hover:underline text-blue-700"
            >
              {row.voucher_applied_booking_ref}
            </Link>
          </div>
        )}
      </td>
      <td className="px-3 py-3 whitespace-nowrap text-xs text-slate-500">
        {fmtDate(row.voucher_expires_at)}
      </td>
      <td className="px-3 py-3 whitespace-nowrap text-xs">
        {row.voucher_redeemed_booking_ref ? (
          <>
            <div className="font-mono">
              <Link
                to={`/admin/bookings/${encodeURIComponent(row.voucher_redeemed_booking_ref)}`}
                className="hover:underline text-blue-700"
                data-testid={`referral-invite-redeemed-booking-${row.id}`}
              >
                {row.voucher_redeemed_booking_ref}
              </Link>
            </div>
            <div className="text-slate-500 mt-0.5">{fmtDate(row.voucher_redeemed_at)}</div>
          </>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="px-3 py-3 text-right whitespace-nowrap">
        <div className="inline-flex items-center gap-1">
          {inviteUrl && (
            <button
              type="button"
              data-testid={`referral-invite-copy-url-${row.id}`}
              title="Copy invite URL"
              onClick={() => copyToClipboard(inviteUrl, "Invite URL copied")}
              className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </button>
          )}
          {row.voucher_code && (
            <button
              type="button"
              data-testid={`referral-invite-copy-code-${row.id}`}
              title="Copy voucher code"
              onClick={() => copyToClipboard(row.voucher_code, "Voucher code copied")}
              className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
